import PubNub from 'pubnub'
import type { Dispatch } from 'redux'
import { PUBNUB_PUBLISH_KEY, PUBNUB_SUBSCRIBE_KEY } from '~/shared/lib/config'
import { queueNotification } from '~/shared/notifications/actions'
import { disconnectFromPubNub, PresenceSignalMessage, providerInboxChannel } from '../pubnub'
import chatSlice from './slice'
import type { MeState } from '~/app/store/reducers/me'
import type { RootState } from '~/app/store/reducers'
import { logger } from '~/shared/lib/logger'

/**
 * Statuses the event engine reports once it parks the subscribe loop: its retries are spent, or
 * the failure is one it never retries (a 403 or 400). The loop stays parked until `reconnect()`.
 */
const PARKED_STATUS_CATEGORIES: ReadonlySet<string> = new Set([
  'PNConnectionErrorCategory',
  'PNDisconnectedUnexpectedlyCategory',
])

/** Floor between recoveries, so a persistent failure cannot hot-loop. */
const RECOVERY_COOLDOWN_MS = 15_000

/**
 * Hands `token` to the session's PubNub client, building and subscribing it on first use.
 *
 * The client calls `fetchChatToken` when a 403 parks its subscribe loop.
 */
export const initPubnub =
  (
    me: { id: NonNullable<MeState['id']> } & {
      provider_fields: Pick<NonNullable<MeState['provider_fields']>, 'tenant'>
    },
    token: string,
    uuid: string,
    fetchChatToken: () => Promise<{ token: string }>,
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const liveClient = getState().chat.pubnub
    if (liveClient) {
      liveClient.setToken(token)
      return
    }

    const pubnub = new PubNub({
      publishKey: PUBNUB_PUBLISH_KEY,
      subscribeKey: PUBNUB_SUBSCRIBE_KEY,
      ssl: true,
      // Explicit because the parked-loop recovery below is written for the event engine's statuses.
      enableEventEngine: true,
      userId: uuid,
    })
    pubnub.setToken(token)

    pubnub.subscribe({
      channels: [providerInboxChannel(me)],
      withPresence: true,
    })

    let lastRecoveryAt = 0
    const recoverParkedLoop = (status: PubNub.Status | PubNub.StatusEvent) => {
      const now = Date.now()
      if (now - lastRecoveryAt < RECOVERY_COOLDOWN_MS) {
        logger.warn('[Chat] PubNub subscribe loop parked within the recovery cooldown', status)
        return
      }
      logger.warn('[Chat] PubNub subscribe loop parked; recovering', status)
      lastRecoveryAt = now
      if (status.error !== 'PNAccessDeniedCategory') {
        pubnub.reconnect()
        return
      }
      fetchChatToken()
        .then(({ token: freshToken }) => {
          pubnub.setToken(freshToken)
          pubnub.reconnect()
        })
        .catch(error => logger.error('[Chat] re-authenticate a parked PubNub loop', error))
    }

    pubnub.addListener({
      signal: signalEvent => {
        // Publisher is the provider sender uuid sending us the update, formatted like "uuid-12"
        const providerUserId = signalEvent.publisher?.split('-')[1]
        if (!providerUserId) return
        const message = signalEvent.message as PresenceSignalMessage
        // This is a presence signal
        dispatch(
          chatSlice.actions.setPresenceState({
            providerUserId,
            presenceState: {
              viewing: message.v,
              typing: message.t,
              patientId: message.p,
            },
          }),
        )
      },
      // The client restores every subscription itself when the network comes back.
      status: status => {
        if (PARKED_STATUS_CATEGORIES.has(status.category)) {
          recoverParkedLoop(status)
        } else if (status.category === PubNub.CATEGORIES.PNNetworkDownCategory) {
          logger.error('[Chat] PubNub network down', status)
          dispatch(
            queueNotification({
              message: 'Received unexpected messaging failure (PNNetworkDownCategory)',
              variant: 'error',
            }),
          )
        }
      },
    })

    // When window closes
    window.addEventListener('beforeunload', e => {
      disconnectFromPubNub(pubnub, me)
    })

    dispatch(chatSlice.actions.setPubnubClient(pubnub))
  }

export interface UpdateChatUnreadParams {
  patient: number
  currentReadAt?: string | null
  threadUid: string
}
